export interface IBcfImportState {
  // File & Analysis
  selectedFile: File | undefined;
  fileAnalysisResult: IBcfAnalysisResponse | undefined;

  // Workflow State
  currentStep: BcfImportStep;
  isStepValid: boolean;

  // Data Mapping (Step 2)
  mappingConfigs: IBcfMappingConfig;

  // Settings (Step 3)
  importSettings: IBcfImportSettings;

  // Summary (Step 4)
  importPreview: IBcfImportPreview | undefined;

  // Import Result (Step 5)
  importResult: IBcfImportResult | undefined;

  // Loading & Errors
  isLoading: boolean;
  isSaving: boolean;
  importProgress: number; // 0-100
  error: string | undefined;
  successMessage: string | undefined;
}

export enum BcfImportStep {
  FILE_UPLOAD = 1,
  DATA_MAPPING = 2,
  SETTINGS = 3,
  SUMMARY = 4,
  EXECUTE = 5,
}

export interface ITopicPreview {
  guid: string;
  title: string;
  description?: string;
  topicStatus?: string;
  topicType?: string;
  priority?: string;
  creationAuthor?: string;
  assignedTo?: string;
  dueDate?: string;
  viewpointCount: number;
  labels: string[];
}

export interface IBcfAnalysisResponse {
  fileId: string;
  bcfValues: {
    types: string[];
    statuses: string[];
    priorities: string[];
    users: string[];
    stages: string[];
    labels: string[];
    totalTopics: number;
    topicsWithViewpoints: number;
    totalViewpoints: number;
    topics: ITopicPreview[];
  };
}

export interface IBcfMappingConfig {
  status: IBcfFieldMapping;
  type: IBcfFieldMapping;
  priority: IBcfFieldMapping;
  user: IBcfFieldMapping;
  stage: IBcfFieldMapping;
  labels: IBcfFieldMapping;
}

export interface IBcfFieldMapping {
  bcfValues: string[];
  mappingRules: Record<string, string | number>; // bcfValue => coordlyValue
  isValid: boolean;
  validationError?: string;
  isIgnored?: boolean; // For optional fields (priority, user, stage, labels) - if true, field is ignored
}

export interface IBcfImportSettings {
  updateExistingIssues: boolean;
  createNewActivities: boolean;
  attachFiles: boolean;
  skipUnmappedItems: boolean;
  defaultAssignee?: string;
  defaultStage?: string;
  defaultDisciplineId?: number;
  defaultPointName?: string;
}

export interface IBcfImportPreview {
  totalToImport: number;
  statusDistribution: Record<string, number>;
  priorityDistribution: Record<string, number>;
  typeDistribution: Record<string, number>;
  mappingCompleteness: number; // percentage
  potentialConflicts: IBcfConflict[];
  estimatedDuration?: number; // seconds
}

export interface IBcfConflict {
  type: 'unmapped' | 'duplicate' | 'invalid' | 'warning';
  message: string;
  affectedItems: number;
}

export interface IBcfImportResult {
  success: boolean;
  queued: boolean;
  message: string;
  totalImported: number;
  totalFailed: number;
  totalTopics: number;
  warnings: string[];
  errors: string[];
  succeededItems: IBcfImportedItem[];
  failedItems: IBcfImportedItem[];
}

export interface IBcfImportedItem {
  id: string;
  title: string;
  status: 'success' | 'failed';
  message: string;
}

export interface IBcfDefaults {
  topicTypes: ITopicType[];
  topicStatus: ITopicStatus[];
  stages: IObraStage[];
  priorities: IPriority[];
  disciplines: INomenclatura[];
  users: IUser[];
}

export interface ITopicType {
  id: string;
  name: string;
}

export interface ITopicStatus {
  id: string;
  name: string;
  color?: string;
}

export interface IObraStage {
  id: string | number;
  name: string;
}

export interface IPriority {
  id: string | number;
  name: string;
  value: number;
}

export interface INomenclatura {
  id: string | number;
  name: string;
  color?: string;
}

export interface IUser {
  id: string;
  name: string;
  email: string;
}
